/**
 * CountryFilter Component
 * 
 * Multi-select dropdown for filtering assets by country.
 * UI/UX Pro Max: Glassmorphism, refined dark mode (via CSS vars), and smooth interactions.
 */
import { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Globe, Search, X, Check } from 'lucide-react'
import { useCountryFilter } from '@/hooks/useCountryFilter'
import { getCountryFlag, formatCount, normalizeSearchText } from '@/lib/countryUtils'
import { cn } from '@/lib/cn'

interface CountryFilterProps {
    className?: string
}

export function CountryFilter({ className }: CountryFilterProps) {
    const {
        selectedCountries,
        availableCountries,
        isLoading,
        showFilter,
        toggleCountry,
        selectAll,
        clearSelection
    } = useCountryFilter()

    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Ensure availableCountries is always an array (safety check for rehydration)
    const safeAvailableCountries = Array.isArray(availableCountries) ? availableCountries : []

    // Filter countries by search query and sort selected to top
    const filteredCountries = useMemo(() => {
        if (!safeAvailableCountries.length) return []

        let result = safeAvailableCountries

        // 1. Filter with diacritics-insensitive matching
        if (searchQuery.trim()) {
            const normalizedQuery = normalizeSearchText(searchQuery)
            result = result.filter(c =>
                normalizeSearchText(c?.name ?? '').includes(normalizedQuery) ||
                normalizeSearchText(c?.code ?? '').includes(normalizedQuery)
            )
        }

        // 2. Sort: Selected first
        // Create a shallow copy to avoid mutating original array if we need to sort
        return [...result].sort((a, b) => {
            const aSelected = selectedCountries.includes(a.code)
            const bSelected = selectedCountries.includes(b.code)

            if (aSelected && !bSelected) return -1
            if (!aSelected && bSelected) return 1
            return 0 // Keep original order (alphabetical) for same status
        })
    }, [safeAvailableCountries, searchQuery, selectedCountries])

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchQuery('')
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isOpen])

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false)
            setSearchQuery('')
        }
    }

    const selectedCount = selectedCountries.length
    const isAllSelected = filteredCountries.length > 0 &&
        filteredCountries.every(c => selectedCountries.includes(c.code))

    // Don't render if filter shouldn't be shown (AFTER all hooks to satisfy Rules of Hooks)
    if (!showFilter) return null

    return (
        <div
            ref={dropdownRef}
            className={cn('relative z-50', className)}
            onKeyDown={handleKeyDown}
        >
            {/* Trigger Button - Using CSS variables for theme support */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
                className={cn(
                    // Layout
                    'relative flex items-center gap-2.5 pl-3 pr-2.5 py-2',
                    'min-w-[140px] justify-between',

                    // Glassmorphism & Theme Colors
                    'bg-[var(--glass-bg)] backdrop-blur-md',
                    'border border-[var(--glass-border)]',
                    'shadow-sm hover:shadow-md',

                    // Typography
                    'text-sm font-medium',
                    'text-[var(--color-text-primary)]',

                    // Interaction
                    'hover:bg-[var(--color-surface-hover)]',
                    'hover:border-[var(--color-border)]',
                    'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]/20',
                    'transition-all duration-200 ease-in-out',

                    // Shape
                    'rounded-xl',

                    // State
                    'cursor-pointer',
                    isLoading && 'opacity-50 cursor-wait'
                )}
                aria-expanded={isOpen}
            >
                <div className="flex items-center gap-2">
                    <div className={cn(
                        "p-1 rounded-md transition-colors",
                        selectedCount > 0
                            ? "bg-[var(--color-surface-highlight)] text-[var(--color-primary-600)]"
                            : "bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
                    )}>
                        <Globe className="w-3.5 h-3.5" />
                    </div>

                    <span className="truncate max-w-[100px]">
                        {selectedCount === 0 ? 'All Countries' :
                            selectedCount === 1 ? safeAvailableCountries.find(c => c.code === selectedCountries[0])?.name :
                                `${selectedCount} Selected`}
                    </span>
                </div>

                {/* Badge for multiple selections */}
                <div className="flex items-center gap-1.5">
                    {selectedCount > 0 && selectedCount !== 1 && (
                        <span className={cn(
                            'flex items-center justify-center',
                            'min-w-[1.25rem] h-5 px-1',
                            'text-[10px] font-bold',
                            'bg-[var(--color-primary-500)] text-white',
                            'rounded-md shadow-sm'
                        )}>
                            {selectedCount}
                        </span>
                    )}

                    <ChevronDown className={cn(
                        'w-4 h-4 text-[var(--color-text-muted)]',
                        'transition-transform duration-200 ease-out',
                        isOpen && 'rotate-180 text-[var(--color-primary-500)]'
                    )} />
                </div>
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className={cn(
                    'absolute top-full right-0 mt-2',
                    'w-80 overflow-hidden',

                    // Style
                    'bg-[var(--color-surface)]',
                    'border border-[var(--color-border)]',
                    'rounded-2xl',

                    // Shadow
                    'shadow-2xl shadow-black/10',

                    // Animation
                    'animate-in fade-in slide-in-from-top-2 duration-200',
                    'origin-top-right'
                )}>
                    {/* Search Header */}
                    <div className="p-3 bg-[var(--color-surface-hover)] border-b border-[var(--color-border-subtle)] space-y-3">
                        <div className="relative group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] group-focus-within:text-[var(--color-primary-500)] transition-colors" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search available countries..."
                                className={cn(
                                    'w-full pl-9 pr-9 py-2.5',
                                    'bg-[var(--color-surface)]',
                                    'border border-[var(--color-border)]',
                                    'rounded-xl',
                                    'text-sm text-[var(--color-text-primary)]',
                                    'placeholder:text-[var(--color-text-muted)]',
                                    'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]/20 focus:border-[var(--color-primary-500)]',
                                    'transition-all duration-200'
                                )}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] rounded-full transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Quick Actions Bar */}
                        <div className="flex items-center justify-between px-1">
                            <button
                                onClick={selectAll}
                                className={cn(
                                    'text-xs font-semibold px-2 py-1 rounded-md transition-colors',
                                    isAllSelected
                                        ? 'text-[var(--color-primary-600)] bg-[var(--color-surface-highlight)]'
                                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-primary-500)] hover:bg-[var(--color-surface-hover)]'
                                )}
                            >
                                Select All
                            </button>

                            <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                                {filteredCountries.length} Countries
                            </span>

                            <button
                                onClick={clearSelection}
                                disabled={selectedCount === 0}
                                className={cn(
                                    'text-xs font-semibold px-2 py-1 rounded-md transition-colors',
                                    selectedCount > 0
                                        ? 'text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20'
                                        : 'text-[var(--color-text-muted)] opacity-50 cursor-not-allowed'
                                )}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Country List */}
                    <ul
                        className={cn(
                            "max-h-[320px] overflow-y-auto custom-scrollbar"
                        )}
                        role="listbox"
                        aria-multiselectable="true"
                    >
                        {filteredCountries.length === 0 ? (
                            <li className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="w-12 h-12 rounded-full bg-[var(--color-surface-hover)] flex items-center justify-center mb-3">
                                    <Globe className="w-6 h-6 text-[var(--color-text-muted)]" />
                                </div>
                                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                                    No countries found
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Try searching for something else
                                </p>
                            </li>
                        ) : (
                            filteredCountries.map((country) => {
                                const isSelected = selectedCountries.includes(country.code)

                                return (
                                    <li key={country.code}>
                                        <button
                                            onClick={() => toggleCountry(country.code)}
                                            role="option"
                                            aria-selected={isSelected}
                                            className={cn(
                                                'group w-full flex items-center px-4 py-3 gap-3',
                                                'text-left transition-all duration-200',
                                                'border-l-[3px]',
                                                isSelected
                                                    ? 'bg-[var(--color-surface-highlight)] border-[var(--color-primary-500)]'
                                                    : 'bg-transparent border-transparent hover:bg-[var(--color-surface-hover)] hover:border-[var(--color-border)]'
                                            )}
                                        >
                                            {/* Flag & Checkbox Container */}
                                            <div className="relative flex items-center justify-center w-6 h-6">
                                                <span className={cn(
                                                    'absolute inset-0 flex items-center justify-center text-xl transition-all duration-200',
                                                    isSelected ? 'opacity-0 scale-50' : 'opacity-100 scale-100'
                                                )}>
                                                    {getCountryFlag(country.code)}
                                                </span>
                                                <div className={cn(
                                                    'absolute inset-0 flex items-center justify-center rounded-md border text-white transition-all duration-200 transform',
                                                    isSelected
                                                        ? 'opacity-100 scale-100 bg-[var(--color-primary-500)] border-[var(--color-primary-500)] rotate-0'
                                                        : 'opacity-0 scale-75 border-[var(--color-border)] bg-[var(--color-surface)] -rotate-90'
                                                )}>
                                                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                                                </div>
                                            </div>

                                            {/* Name & Count */}
                                            <div className="flex-1 flex flex-col">
                                                <span className={cn(
                                                    'text-sm font-medium transition-colors',
                                                    isSelected
                                                        ? 'text-[var(--color-primary-700)] dark:text-[var(--color-primary-400)]'
                                                        : 'text-[var(--color-text-primary)]'
                                                )}>
                                                    {country.code} - {country.name}
                                                </span>
                                                <span className={cn(
                                                    'text-[10px] tracking-wide transition-colors',
                                                    isSelected ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-text-muted)]'
                                                )}>
                                                    {formatCount(country.count)} assets
                                                </span>
                                            </div>
                                        </button>
                                    </li>
                                )
                            })
                        )}
                    </ul>

                    {/* Status Footer */}
                    <div className="px-4 py-2.5 bg-[var(--color-surface-hover)] border-t border-[var(--color-border-subtle)]">
                        <p className={cn(
                            "text-xs text-center transition-colors",
                            selectedCount === 0 ? "text-amber-500 font-medium" : "text-[var(--color-text-muted)]"
                        )}>
                            {selectedCount === 0
                                ? 'Select a country to view assets'
                                : `Viewing ${selectedCount} of ${safeAvailableCountries.length} countries`
                            }
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
